import { useTranslation } from 'react-i18next';
import {
  buildGrossMarginRow,
  buildOtherRows,
  buildTonnageRows,
} from '../Sections/SectionKeyNumber';
import PdfDocument from '../../PDF/PdfDocument';
import { sortCultures } from '../utils/cultureSort';

const hasConstraint = (configConstraints, name) =>
  configConstraints?.some((c) => c.name === name || c[name] === true);

const buildPacTable = (suggest, previous, forecastCampaign) => {
  if (!suggest?.years?.[0]?.capPoints) return [];

  const pacTable = suggest.years[0].capPoints.map((m) => ({ categorie: m.categorie, data: [] }));
  pacTable.push({ categorie: 'Total', data: [] });

  if (previous?.capPoints) {
    const prevPoints = Array.isArray(previous.capPoints[0])
      ? previous.capPoints
      : [previous.capPoints];

    prevPoints.forEach((pointsArray) => {
      let total = 0;
      pacTable.forEach((row) => {
        if (row.categorie === 'Total') {
          row.data.push({ value: total });
        } else {
          const found = pointsArray?.find((p) => p.categorie === row.categorie);
          const value = found?.points || 0;
          row.data.push({ value });
          total += value;
        }
      });
    });
  }

  suggest.years.forEach((year) => {
    let total = 0;
    year.capPoints?.forEach((p) => {
      const row = pacTable.find((r) => r.categorie === p.categorie);
      if (row) {
        row.data.push({ value: p.points });
        total += p.points;
      }
    });
    pacTable.find((r) => r.categorie === 'Total')?.data.push({ value: total });
  });

  return pacTable;
};

const buildPacHelps = (capHelps) => {
  if (!capHelps?.length) return [];
  return capHelps.map((elt) => `${elt} €`);
};

const enrichSuggest = (suggest, exploitation) => {
  if (!suggest?.years || !suggest?.parcels) return suggest;

  const prevCampaignKey = `${exploitation.forecastCampaign.startYear - 1}-${exploitation.forecastCampaign.endYear - 1}`;

  const enrichedParcels = suggest.parcels.map((parcel) => {
    const exploitParcel = exploitation.parcels?.find((p) => p.id === parcel.id);
    const previousCulture = exploitParcel?.parcelCultures?.[prevCampaignKey];

    const previousCultureEntry = previousCulture
      ? [{ id: previousCulture.id, name: previousCulture.name, type: 'main' }]
      : [];

    return {
      ...parcel,
      cultures: [previousCultureEntry, ...parcel.cultures],
    };
  });

  const enrichedYears = suggest.years.map((year) => {
    if (!Array.isArray(year.irrigationDistribution)) return year;
    const irrigationDistribution = year.irrigationDistribution.map((irrigationValue, index) => ({
      parcelId: suggest.parcels[index]?.id,
      irrigationValue,
    }));
    return { ...year, irrigationDistribution };
  });

  return { ...suggest, parcels: enrichedParcels, years: enrichedYears };
};

const getVal = (nitrogenData, field, unit = 'currency') => {
  if (!nitrogenData) return 0;
  const entry = nitrogenData[field];
  if (entry == null) return 0;
  if (typeof entry === 'number') return entry;
  return (unit === 'unit' ? entry.unit : entry.currency) ?? 0;
};

const getValueFromNitrogen = (nitrogenData, row, unit = 'currency') => {
  if (!nitrogenData) return 0;

  if (row.computed === 'needToProvide') {
    return (
      getVal(nitrogenData, 'quantity', unit) -
      getVal(nitrogenData, 'cropGain', unit) -
      getVal(nitrogenData, 'coverGain', unit)
    );
  }

  if (row.gainCategory) {
    const list = nitrogenData.gainPerCropPerHectareList?.[row.gainCategory];
    const values = unit === 'unit' ? list?.unit : list?.currency;
    if (!values?.length) return 0;
    return values.reduce((sum, val) => sum + (val || 0), 0);
  }

  if (row.gainCategory2 != null && row.cultureIndex != null) {
    const list = nitrogenData.gainPerCropPerHectareList?.[row.gainCategory2];
    const values = unit === 'unit' ? list?.unit : list?.currency;
    return values?.[row.cultureIndex] ?? 0;
  }

  const entry = nitrogenData[row.id];
  if (entry == null) return 0;
  if (typeof entry === 'number') return entry;
  return (unit === 'unit' ? entry.unit : entry.currency) ?? 0;
};

/**
 * @param {object} suggest
 * @param {object} previous
 * @param {array}  cultures
 * @param {string} unit
 * @param {function} t
 */
export const buildNitrogenRows = (suggest, previous, cultures, unit = 'currency', t) => {
  const years = suggest?.years;
  if (!years?.length || !years.some((y) => y?.nitrogen)) return [];

  const hasPrevious = !!previous?.nitrogen;

  const baseRows = [
    { id: 'nitrogenCapital', indicator: t('simulation.result.nitrogen.nitrogenCapital') },
    { id: 'coverGain', indicator: t('simulation.result.nitrogen.coverGain') },
    { id: 'quantity', indicator: t('simulation.result.nitrogen.totalNeed') },
    { id: 'cropGain', indicator: t('simulation.result.nitrogen.cropGain') },
    {
      id: 'nitrogenFixerSum',
      indicator: t('simulation.result.nitrogen.nitrogenFixerGain'),
      gainCategory: 'nitrogenFixer',
    },
    {
      id: 'othersSum',
      indicator: t('simulation.result.nitrogen.othersGain'),
      gainCategory: 'others',
    },
    {
      id: 'needToProvide',
      indicator: t('simulation.result.nitrogen.needToProvide'),
      computed: 'needToProvide',
    },
  ];

  return baseRows.map((row) => {
    const hierarchy = row.gainCategory ? ['cropGain', row.id] : [row.id];

    const rowData = {
      ...row,
      hierarchy,
      isChild: hierarchy.length > 1,
    };

    if (hasPrevious) {
      rowData.previous = getValueFromNitrogen(previous.nitrogen, row, unit);
    }

    years.forEach((year, index) => {
      rowData[`year_${index}`] = getValueFromNitrogen(year?.nitrogen, row, unit);
    });

    return rowData;
  });
};

const usePdfData = () => {
  const { t } = useTranslation();

  /**
   *
   * @param {object} params
   * @param {object} params.resultData
   * @param {string} params.suggestionKey
   * @param {object} params.exploitation
   * @param {object} params.user
   * @param {string} params.sector
   * @param {object} params.cooperative
   * @param {string|null} params.graphMarge
   * @param {object} params.mapData
   * @param {object} params.culturesColors
   */
  const buildPdfData = ({
    resultData,
    suggestionKey,
    exploitation,
    user,
    sector,
    cooperative,
    graphMarge = null,
    graphInterculturePerYear,
    stackedCharts,
    mapData = null,
    culturesColors = [],
    culturesExploit,
    graphPerYear = [],
  }) => {
    const result = resultData.result;
    const suggest = result?.suggestions?.[suggestionKey];
    const previous = result?.previous;
    const config = resultData.currentSimulationResult?.config;
    const configConstraints = config?.constraints ?? [];
    const forecastCampaign = resultData.forecastCampaign;
    const configCultures = config?.cultures ?? [];

    const cultures = culturesExploit
      .filter((c) => result?.cultures?.find((rc) => rc.id === c.id))
      .map((c) => {
        const configCulture = configCultures.find((cc) => cc.id === c.id);
        return {
          ...c,
          constraint:
            configCulture?.minMax?.min > 0 || configCulture?.minMax?.max > 0
              ? {
                  min: configCulture.minMax.min,
                  max: configCulture.minMax.max,
                  type: configCulture.minMax.unit ?? 'ha',
                }
              : undefined,
        };
      });

    const isNitrogenASelectedConstraint = hasConstraint(configConstraints, 'nitrogen');
    const isCoverProposalASelectedConstraint = hasConstraint(configConstraints, 'coverProposal');
    const isExtraBiomassASelectedConstraint = hasConstraint(configConstraints, 'extraBiomass');

    const grossMarginRow = buildGrossMarginRow(resultData.grossMarginTableData, t);
    const otherRows = buildOtherRows(suggest, previous, configConstraints, result, t);
    const keyNumber = [...(grossMarginRow ? [grossMarginRow] : []), ...otherRows];

    const keyNumberNitrogen = isNitrogenASelectedConstraint
      ? buildNitrogenRows(suggest, previous, result?.cultures, 'currency', t)
      : [];

    const pac = buildPacTable(suggest, previous, forecastCampaign);
    const pacHelps = buildPacHelps(result?.capHelps);

    const invertedCultures = (result?.cultures ?? []).filter((c) => c.customizedFor === 'organic');

    const obligationRestrictions = config?.obligationRestriction ?? [];

    const dataCropGrossMargin = mapData?.getMapParcelsData?.() ?? null;

    const previousCropMargin = result?.previous?.culturesDistribution ?? null;

    const enrichedSuggest = enrichSuggest(suggest, exploitation);

    const hasPacConstraint = configConstraints?.find((c) => c.name === 'activateCAPCategory')?.min;

    const uniqueIds = new Set();
    const uniqueCultures = sortCultures(
      cultures.filter((c) => !uniqueIds.has(c.id) && uniqueIds.add(c.id)),
    );
    const tonnageRows = buildTonnageRows(suggest, previous, uniqueCultures, t).filter(
      (row) => row.id === 'Tonnage (t)',
    );

    const constraints = hasPacConstraint
      ? {
          ...result?.constraints,
          pac: {
            displayName: t('simulation.result.pdf.constraints.pacPoints'),
            min: hasPacConstraint,
            max: hasPacConstraint,
          },
        }
      : result?.constraints;

    return {
      exploitation,
      cooperative,
      user,
      sector,
      date: new Date(),
      jobId: resultData.currentSimulationResult?.jobId,
      proposal: suggestionKey,
      forecastCampaign,
      suggest: enrichedSuggest,
      previous,
      cultures: cultures,
      invertedCultures,
      tonnageRows,

      keyNumber,
      keyNumberNitrogen,

      pac,
      pacHelps,

      constraints,
      obligationRestrictions,

      graphMarge,
      graphPerYear,
      graphInterculturePerYear,
      stackedCharts,

      dataCropGrossMargin,
      previousCropMargin,
      culturesColors,

      isCoverProposalASelectedConstraint,
      isNitrogenASelectedConstraint,
      isExtraBiomassASelectedConstraint,
    };
  };

  const downloadPdf = async ({ pdfData, results, exploitationName, skipSave = false }) => {
    const pdfDoc = await PdfDocument(
      { ...results, constraints: pdfData.constraints },
      pdfData.proposal,
      pdfData.exploitation,
      pdfData.user,
      pdfData.sector,
      pdfData.graphMarge,
      pdfData.keyNumber,
      pdfData.keyNumberNitrogen,
      pdfData.graphPerYear,
      pdfData.graphInterculturePerYear,
      pdfData.stackedCharts,
      pdfData.cultures,
      pdfData.invertedCultures,
      pdfData.tonnageRows,
      pdfData.suggest,
      pdfData.cooperative,
      pdfData.pac,
      pdfData.pacHelps,
      pdfData.jobId,
      pdfData.forecastCampaign,
      pdfData.obligationRestrictions,
      pdfData.isCoverProposalASelectedConstraint,
      pdfData.isNitrogenASelectedConstraint,
      pdfData.isExtraBiomassASelectedConstraint,
      pdfData.dataCropGrossMargin,
      pdfData.previousCropMargin,
      pdfData.culturesColors,
    );

    if (!skipSave) {
      const date = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const dformat =
        date.getFullYear() +
        pad(date.getMonth() + 1) +
        pad(date.getDate()) +
        pad(date.getHours()) +
        pad(date.getMinutes()) +
        pad(date.getSeconds());
      pdfDoc.pdf.save(`Assolement_${dformat}_${exploitationName}.pdf`);
    }

    return pdfDoc.pdf;
  };

  return { buildPdfData, downloadPdf };
};

export default usePdfData;
